import { useCallback, useContext, useEffect } from 'react';

import {
  UNSAFE_NavigationContext as NavigationContext,
  useLocation,
} from 'react-router-dom';

import PropTypes from 'prop-types';

// Note this component should not live for a long period of time. We are using an UNSAVE api
// in order to get us by while we move to the new `createBrowserRouter` api in React Router v6.
// Then we will have access to the built in hooks for managing blocking and prompting.

export default function Prompt({
  message = 'Are you sure you want to navigate away? You’ll lose your changes.',
  when,
}) {
  usePrompt(message, when);

  return null;
}

Prompt.propTypes = {
  message: PropTypes.string,
  when: PropTypes.bool.isRequired,
};

function useConfirmExit(confirmExit, when = true) {
  const { navigator } = useContext(NavigationContext);
  const { pathname: currentPathname } = useLocation();

  useEffect(() => {
    // To prevent a horrible developer experience with file change reloading
    // within development mode we turn this feature off. So you can add a
    // Prompt component without the constant confirm dialog popping up.
    if (!when || process.env.NODE_ENV === 'development') {
      return;
    }

    const push = navigator.push;

    navigator.push = (to, ...rest) => {
      // Only prompt when actually leaving the current route. Same-path pushes
      // that change only the query string or hash (e.g. table pagination via
      // setSearchParams) keep the form mounted, so no changes are lost.
      const nextPathname =
        typeof to === 'string' ? to.split(/[?#]/)[0] : to?.pathname;
      const isSamePage = !nextPathname || nextPathname === currentPathname;

      if (isSamePage) {
        push(to, ...rest);
        return;
      }

      const result = confirmExit();
      if (result !== false) {
        push(to, ...rest);
      }
    };

    return () => {
      navigator.push = push;
    };
  }, [navigator, confirmExit, when, currentPathname]);
}

function usePrompt(message, when = true) {
  useEffect(() => {
    if (when) {
      window.onbeforeunload = function () {
        return message;
      };
    }

    return () => {
      window.onbeforeunload = null;
    };
  }, [message, when]);

  const confirmExit = useCallback(() => {
    const confirm = window.confirm(message);
    return confirm;
  }, [message]);
  useConfirmExit(confirmExit, when);
}
